import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Building2, Shield, Search, MapPin, Info, HelpCircle, Phone, BookOpen } from 'lucide-react';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img 
              src="/logo.webp" 
              alt="TesisKirala" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Tesisler Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      isActive('/tesisler') ? 'text-emerald-700' : 'text-slate-700 hover:text-emerald-700'
                    }`}
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Tesisler
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/tesisler?type=hali_saha"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-emerald-50 hover:text-emerald-700 focus:bg-emerald-50"
                            data-testid="nav-hali-saha"
                          >
                            <div className="text-sm font-medium leading-none">⚽ Halı Sahalar</div>
                            <p className="line-clamp-2 text-sm leading-snug text-slate-500">
                              Futbol oynamak için halı saha kirala
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/tesisler?type=kapali_spor_salonu"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-emerald-50 hover:text-emerald-700 focus:bg-emerald-50"
                            data-testid="nav-spor-salonu"
                          >
                            <div className="text-sm font-medium leading-none">🏀 Kapalı Spor Salonları</div>
                            <p className="line-clamp-2 text-sm leading-snug text-slate-500">
                              Basketbol, voleybol ve daha fazlası
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li className="col-span-2">
                        <NavigationMenuLink asChild>
                          <Link
                            to="/tesisler"
                            className="flex items-center gap-2 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-emerald-50 hover:text-emerald-700 focus:bg-emerald-50 bg-slate-50"
                            data-testid="nav-all-facilities"
                          >
                            <MapPin className="w-4 h-4" />
                            <div>
                              <div className="text-sm font-medium leading-none">Tüm Tesisleri Gör</div>
                              <p className="text-sm text-slate-500">Şehir ve ilçeye göre filtrele</p>
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/harita"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-emerald-50 hover:text-emerald-700 focus:bg-emerald-50"
                            data-testid="nav-map"
                          >
                            <div className="text-sm font-medium leading-none">🗺️ Harita ile Ara</div>
                            <p className="line-clamp-2 text-sm leading-snug text-slate-500">
                              Konumuna yakın tesisleri haritada gör
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* SSS */}
                <NavigationMenuItem>
                  <Link 
                    to="/sss"
                    className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                      isActive('/sss') ? 'text-emerald-700' : 'text-slate-700 hover:text-emerald-700'
                    }`}
                    data-testid="nav-faq"
                  >
                    <HelpCircle className="w-4 h-4" />
                    SSS
                  </Link>
                </NavigationMenuItem>

                {/* Hakkımızda */}
                <NavigationMenuItem>
                  <Link 
                    to="/hakkimizda"
                    className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                      isActive('/hakkimizda') ? 'text-emerald-700' : 'text-slate-700 hover:text-emerald-700'
                    }`}
                    data-testid="nav-about"
                  >
                    <Info className="w-4 h-4" />
                    Hakkımızda
                  </Link>
                </NavigationMenuItem>

                {/* Blog */}
                <NavigationMenuItem>
                  <Link 
                    to="/blog"
                    className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                      isActive('/blog') ? 'text-emerald-700' : 'text-slate-700 hover:text-emerald-700'
                    }`}
                    data-testid="nav-blog"
                  >
                    <BookOpen className="w-4 h-4" />
                    Blog
                  </Link>
                </NavigationMenuItem>

                {/* İletişim */}
                <NavigationMenuItem>
                  <Link 
                    to="/iletisim"
                    className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                      isActive('/iletisim') ? 'text-emerald-700' : 'text-slate-700 hover:text-emerald-700'
                    }`}
                    data-testid="nav-contact"
                  >
                    <Phone className="w-4 h-4" />
                    İletişim
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 hover:bg-emerald-50"
                    data-testid="user-menu-trigger"
                  >
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-700" />
                      </div>
                    )}
                    <span className="max-w-[150px] truncate font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm text-slate-500">
                    {user?.email}
                  </div>
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={() => navigate(user?.role === 'user' ? '/hesabim' : '/isletmeci')}
                    data-testid="menu-my-account"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {user?.role === 'user' ? 'Hesabım' : 'İşletmeci Paneli'}
                  </DropdownMenuItem>
                  
                  {(user?.role === 'operator' || user?.role === 'admin') && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/isletmeci')}
                      data-testid="menu-operator"
                      className="hidden" // Hiding redundant link as logic moved above
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      İşletmeci Paneli
                    </DropdownMenuItem>
                  )}
                  
                  {user?.role === 'admin' && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')}
                      data-testid="menu-admin"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Paneli
                    </DropdownMenuItem>
                  )}

                  {/* Customer Panel Access for Operators/Admins */}
                  {(user?.role === 'operator' || user?.role === 'admin') && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/hesabim')}
                      data-testid="menu-my-account-switch"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Müşteri Paneli
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/giris')}
                  className="text-slate-700 hover:text-emerald-700 hover:bg-emerald-50"
                  data-testid="nav-login-btn"
                >
                  Giriş Yap
                </Button>
                <Button 
                  onClick={() => navigate('/giris')}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6"
                  data-testid="nav-register-btn"
                >
                  Kayıt Ol
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4 shadow-lg">
          <div className="flex flex-col gap-1">
            {/* Tesisler */}
            <div className="py-2">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 px-3">Tesisler</p>
              <Link 
                to="/tesisler?type=hali_saha" 
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                ⚽ Halı Sahalar
              </Link>
              <Link 
                to="/tesisler?type=kapali_spor_salonu" 
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                🏀 Kapalı Spor Salonları
              </Link>
              <Link 
                to="/tesisler" 
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <MapPin className="w-4 h-4" />
                Tüm Tesisler
              </Link>
            </div>

            <hr className="border-slate-200 my-2" />

            {/* Diğer Sayfalar */}
            <Link 
              to="/sss" 
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <HelpCircle className="w-4 h-4" />
              SSS
            </Link>
            <Link 
              to="/hakkimizda" 
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Info className="w-4 h-4" />
              Hakkımızda
            </Link>
            <Link 
              to="/iletisim" 
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Phone className="w-4 h-4" />
              İletişim
            </Link>

            <hr className="border-slate-200 my-2" />
            
            {isAuthenticated ? (
              <>
                <Link 
                  to="/hesabim" 
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Hesabım
                </Link>
                
                {(user?.role === 'operator' || user?.role === 'admin') && (
                  <Link 
                    to="/isletmeci" 
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Building2 className="w-4 h-4" />
                    İşletmeci Paneli
                  </Link>
                )}
                
                {user?.role === 'admin' && (
                  <Link 
                    to="/admin" 
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Shield className="w-4 h-4" />
                    Admin Paneli
                  </Link>
                )}
                
                <button 
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 text-left w-full"
                >
                  <LogOut className="w-4 h-4" />
                  Çıkış Yap
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 mt-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigate('/giris');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full"
                >
                  Giriş Yap
                </Button>
                <Button 
                  onClick={() => {
                    navigate('/giris');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  Kayıt Ol
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
